<template>
  <div class="tag-page">
    <div class="back-link-container">
      <a href="/tags/" class="back-link">&larr; All Tags</a>
    </div>

    <h1>{{ page.title }}</h1>

    <section v-if="projects.length" class="tag-section">
      <h2>Projects</h2>
      <div class="content-grid">
        <PageLinkCard
          v-for="item in projects"
          :key="item.url"
          :item="item"
          :image="item.cover_image"
          link-text="View project"
        />
      </div>
    </section>

    <section v-if="articles.length" class="tag-section">
      <h2>Articles</h2>
      <div class="content-grid">
        <PageLinkCard
          v-for="item in articles"
          :key="item.url"
          :item="item"
          link-text="Read article"
        />
      </div>
    </section>

    <section v-if="demos.length" class="tag-section">
      <h2>Demos</h2>
      <div class="content-grid">
        <PageLinkCard
          v-for="item in demos"
          :key="item.url"
          :item="item"
          link-text="View demo"
        />
      </div>
    </section>

    <p
      v-if="!projects.length && !articles.length && !demos.length"
      class="empty-state"
    >
      No content found with this tag.
    </p>
  </div>
</template>

<script setup lang="ts">
import { useData } from "vitepress";
import { computed } from "vue";
import { data as articlesData } from "../../../../articles/index.data";
import { data as demosData } from "../../../../demos/index.data";
import { data as projectsData } from "../../../../projects/index.data";
import PageLinkCard from "./PageLinkCard.vue";

const { page, params } = useData();

const tag = computed(() => params.value?.tag || "");

interface ContentItem {
  url: string;
  title: string;
  date?: string;
  desc?: string;
  tags?: string[];
}

const articles = computed(() => {
  return (articlesData as ContentItem[]).filter((item) =>
    item.tags?.includes(tag.value),
  );
});

const demos = computed(() => {
  return (demosData as ContentItem[]).filter((item) =>
    item.tags?.includes(tag.value),
  );
});

interface ProjectItem extends ContentItem {
  cover_image?: string;
}

const projects = computed(() => {
  return (projectsData as ProjectItem[]).filter((item) =>
    item.tags?.includes(tag.value),
  );
});
</script>

<style scoped lang="scss">
@use "../../styles/abstracts/breakpoints" as *;

.tag-page {
  margin-top: 2rem;
}

.back-link-container {
  margin-bottom: var(--space-lg);
}

.back-link {
  color: var(--color-text-muted);
  text-decoration: none;
  font-size: 0.9rem;
}

.back-link:hover,
.back-link:focus {
  color: var(--color-accent);
}

.tag-section {
  margin-bottom: 3rem;
}

.tag-section h2 {
  margin-bottom: 1.5rem;
}

.content-grid {
  display: grid;
  gap: 2rem;

  @include lg {
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  }
}

.empty-state {
  color: var(--color-text-muted);
  font-style: italic;
}
</style>
